/**
 * Manager Dashboard Data Service
 *
 * Application service that hydrates manager-dashboard datasets required by the
 * store initializer.
 *
 * Why this service exists
 * - Keeps `store-provider` focused on route/state orchestration.
 * - Centralizes manager API orchestration and mapping in one testable boundary.
 * - Returns a stable contract for future manager-dashboard consumers.
 */

import { getManagerSharedScorecards, getManagerSummary } from '@/lib';
import { mapApiManagerDataToFrontend } from '@/utils';

/**
 * Stable output contract for manager dashboard hydration.
 */
export interface ManagerDashboardData {
  /** Mapped manager/team summary model committed to store state. */
  managersTeamSummary: ReturnType<typeof mapApiManagerDataToFrontend>;
  /** Raw shared-scorecards payload fetched for manager context and logging. */
  sharedScorecards: Awaited<ReturnType<typeof getManagerSharedScorecards>>;
}

/**
 * Load manager summary + shared scorecards for a manager dashboard view.
 *
 * Execution flow
 * 1. Fetch manager summary and shared scorecards in parallel.
 * 2. Map summary payload into frontend model.
 * 3. Return both mapped summary and shared-scorecards payload.
 *
 * @param managerId Manager user UUID.
 * @returns Hydrated manager dashboard data.
 */
export const loadManagerDashboardData = async (
  managerId: string,
): Promise<ManagerDashboardData> => {
  const [apiManagerSummary, apiManagerSharedScorecards] = await Promise.all([
    getManagerSummary(managerId),
    getManagerSharedScorecards(managerId),
  ]);

  return {
    managersTeamSummary: mapApiManagerDataToFrontend(apiManagerSummary),
    sharedScorecards: apiManagerSharedScorecards,
  };
};
